import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { queue as toastQueue } from "@/components/Toast";
import { MAX_DATA_FILE_BYTES, isWorkbookFile, prepareDataFile } from "./prepareDataFile";

export type ServerAttachment = {
  id: string;
  fileName: string | null;
  type: string;
  state: "processing" | "completed" | "failed" | string;
  createdAt: string;
  durationSec?: number | null;
  error?: string | null;
  canRetry?: boolean;
  /** Set for a video the agent attached from a link: the post it came from. */
  sourceUrl?: string | null;
  /** Workshop files: who uploaded it. Side chats leave this off. */
  attribution?: {
    createdBy: {
      userId: string | null;
      userName: string | null;
    };
  };
};

type UploadUrlResponse = {
  documentId: string;
  uploadUrl: string;
  uploadToken: string;
};

/**
 * A file entry rendered inline in the chat rail, in upload order.
 *
 * A video only ever passes through `uploading`: it is ready as soon as it is in
 * the bucket, and the agent reads it on demand with AskVideo. PDFs, spreadsheets
 * and text files have a `processing` phase, because they are parsed and indexed
 * up front.
 */
export type ChatAttachmentItem = {
  id: string;
  fileName: string;
  /**
   * "image" only in the workshop, where an attached image joins the project's
   * Media. "spreadsheet" and "text" only in the workshop too.
   */
  kind: AttachmentKind | "image";
  state: "processing" | "completed" | "uploading" | "error";
  createdAt: string;
  error?: string;
  canRetry?: boolean;
  /** Who uploaded it. Set on workshop files; absent on a side chat. */
  authorUserId?: string | null;
  authorName?: string | null;
  /** A video's length, from the server or read in the browser at upload. */
  durationSec?: number | null;
  /** 0 to 1 while the file travels to the bucket. */
  progress?: number;
  /**
   * A frame taken from the video on this device when it was attached. Only this
   * tab has it; after a reload the rail needs a view URL (creative-os-backend#98).
   */
  previewUrl?: string | null;
  /** Still on this device: not registered on the server yet, or refused before it was. */
  local?: boolean;
  /** A video the agent attached from a link: the post it came from. */
  sourceUrl?: string | null;
};

type PendingUpload = {
  tempId: string;
  fileName: string;
  kind: AttachmentKind;
  status: "uploading" | "error";
  createdAt: string;
  error?: string;
  progress?: number;
  previewUrl?: string | null;
  durationSec?: number | null;
};

/*
 * What this tab learned about a file while uploading it, kept by document id so
 * the server's row can carry it once the upload lands. Nothing here survives a
 * reload; the server's view URL will (creative-os-backend#98).
 */
const localPosters = new Map<string, string>();
const localDurations = new Map<string, number>();

/**
 * One frame from a picked video, as a small JPEG, for its thumbnail, and the
 * video's length, from one decode. The poster is null when the browser cannot
 * decode it; the thumbnail then shows a play mark on a plain plane, which is
 * also what a reload shows.
 */
function captureVideoPoster(file: File): Promise<{ poster: string | null; duration: number | null }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    let settled = false;
    const done = (poster: string | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;
      URL.revokeObjectURL(url);
      video.removeAttribute("src");
      resolve({ poster, duration });
    };
    const timer = window.setTimeout(() => done(null), 8000);
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.onloadeddata = () => {
      // A little way in: the very first frame is often black.
      video.currentTime = Number.isFinite(video.duration) ? Math.min(1, video.duration / 4) : 0;
    };
    video.onseeked = () => {
      try {
        const width = 320;
        const height = Math.round((width * video.videoHeight) / (video.videoWidth || 16)) || 180;
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d")?.drawImage(video, 0, 0, width, height);
        done(canvas.toDataURL("image/jpeg", 0.72));
      } catch {
        done(null);
      }
    };
    video.onerror = () => done(null);
    video.src = url;
  });
}

export const VIDEO_MIME_TYPES = [
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "video/x-matroska",
];

/** Extensions browsers commonly report with an empty or wrong `file.type`. */
const VIDEO_EXTENSIONS = [".mp4", ".mov", ".webm", ".mkv"];

export const ATTACHMENT_ACCEPT = `.pdf,application/pdf,${VIDEO_MIME_TYPES.join(",")},${VIDEO_EXTENSIONS.join(",")}`;

/**
 * CSV and text files the workshop agent reads. Matched on extension: browsers
 * report these inconsistently (Windows calls a .csv application/vnd.ms-excel,
 * and a .md often has no type at all), so the upload always sends the canonical
 * mime below, which the signed PUT is bound to.
 */
const DATA_FILE_TYPES: Record<string, { mime: string; kind: "spreadsheet" | "text" }> = {
  ".csv": { mime: "text/csv", kind: "spreadsheet" },
  ".txt": { mime: "text/plain", kind: "text" },
  ".md": { mime: "text/markdown", kind: "text" },
  ".json": { mime: "application/json", kind: "text" },
};

/** The workshop's file picker: PDFs and videos, plus CSV, text and Excel workbooks. */
export const WORKSHOP_FILE_ACCEPT = `${ATTACHMENT_ACCEPT},${Object.keys(DATA_FILE_TYPES).join(",")},.xlsx,.xls`;

export const MAX_VIDEO_BYTES = 200 * 1024 * 1024;
export const MAX_VIDEO_SECONDS = 600;

export type AttachmentKind = "pdf" | "video" | "spreadsheet" | "text";

const ONLY_PDF_AND_VIDEO = "Only PDF and video files (mp4, mov, webm, mkv) are supported";
export const WORKSHOP_FILE_TYPES_MESSAGE =
  "Only PDF, video (mp4, mov, webm, mkv), spreadsheet (csv, xlsx, xls) and text (txt, md, json) files are supported";

/** A side chat's attachments. The agent can add one too (a video from a link). */
export const sessionAttachmentsKey = (brandId: string, sessionId: string | null) => [
  "sessionAttachments",
  brandId,
  sessionId,
];

/**
 * Where a chat's attachments live. Defaults to the side chats' brand
 * endpoints, where a file belongs to its chat; the workshop points this at its
 * project endpoints, where a file belongs to the project.
 */
export type AttachmentEndpoints = {
  queryKey: (sessionId: string | null) => unknown[];
  /** GET, with `sessionId` as a query param. */
  list: string;
  /** Pulls the attachment array out of the list response. */
  parseList: (data: unknown) => ServerAttachment[];
  uploadUrl: string;
  process: string;
  retry: (documentId: string) => string;
  /** DELETE for one file. Absent where files cannot be removed from the chat. */
  remove?: (documentId: string) => string;
  /** The new document's id, read off the process response. */
  processedId: (data: unknown) => string | null;
  /** Other queries that show the same files, refreshed when one lands. */
  alsoInvalidate?: unknown[][];
  /** Spreadsheets and text files too (the workshop). Side chats take PDFs and videos. */
  acceptsDataFiles?: boolean;
};

function brandEndpoints(brandId: string): AttachmentEndpoints {
  return {
    queryKey: (sessionId) => sessionAttachmentsKey(brandId, sessionId),
    list: `/brand/${brandId}/attachments`,
    parseList: (data) => data as ServerAttachment[],
    uploadUrl: `/brand/${brandId}/attachments/upload-url`,
    process: `/brand/${brandId}/attachments/process`,
    retry: (documentId) => `/brand/${brandId}/attachments/${documentId}/retry`,
    remove: (documentId) => `/brand/${brandId}/attachments/${documentId}`,
    processedId: (data) => (data as { id?: string } | null)?.id ?? null,
  };
}

function extensionOf(fileName: string): string {
  const idx = fileName.lastIndexOf(".");
  return idx >= 0 ? fileName.slice(idx + 1).toLowerCase() : "";
}

export function isVideoFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    VIDEO_MIME_TYPES.includes(file.type) ||
    VIDEO_EXTENSIONS.some((ext) => name.endsWith(ext))
  );
}

export function isPdfFile(file: File): boolean {
  return (
    file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
  );
}

function dataFileType(file: File) {
  const ext = extensionOf(file.name);
  return ext ? DATA_FILE_TYPES[`.${ext}`] : undefined;
}

/**
 * A CSV, text file or Excel workbook the workshop accepts. Each goes through
 * prepareDataFile before upload (a workbook becomes one CSV per sheet).
 */
export function isDataFile(file: File): boolean {
  return !!dataFileType(file) || isWorkbookFile(file);
}

/** How a picked file is shown and validated. */
export function attachmentKindOf(file: File): AttachmentKind {
  if (isVideoFile(file)) return "video";
  if (isWorkbookFile(file)) return "spreadsheet";
  return dataFileType(file)?.kind ?? "pdf";
}

/** A server row's `type` as a chip kind; anything unknown reads as a document. */
export function attachmentKindOfType(type: string): AttachmentKind {
  return type === "video" || type === "spreadsheet" || type === "text" ? type : "pdf";
}

function mimeFor(file: File): string {
  const data = dataFileType(file);
  if (data) return data.mime;
  if (file.type) return file.type;
  const name = file.name.toLowerCase();
  if (name.endsWith(".mov")) return "video/quicktime";
  if (name.endsWith(".webm")) return "video/webm";
  if (name.endsWith(".mkv")) return "video/x-matroska";
  if (name.endsWith(".mp4")) return "video/mp4";
  return "application/pdf";
}

/**
 * Duration is only readable in the browser, so it is checked here and sent along
 * as metadata rather than enforced server-side. Resolves undefined when the
 * browser cannot decode the container — the upload then proceeds and the
 * analyser is left to reject it, which is better than blocking a valid file.
 */
function readVideoDuration(file: File): Promise<number | undefined> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    let settled = false;
    const done = (value: number | undefined) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(value);
    };
    // A container that neither loads nor errors must not hold the upload forever.
    const timer = window.setTimeout(() => done(undefined), 8000);
    video.preload = "metadata";
    video.onloadedmetadata = () =>
      done(Number.isFinite(video.duration) ? video.duration : undefined);
    video.onerror = () => done(undefined);
    video.src = url;
  });
}

/**
 * Upload one picked file. A CSV, text file or workbook is prepared first (see
 * prepareDataFile), and a workbook can become several CSVs, so this resolves
 * with every process response, in order. Shared by the chat strip and the
 * workshop's Sources tab.
 */
export async function uploadPickedFile(
  file: File,
  target: { uploadUrl: string; process: string; sessionId: string | null },
  /** 0 to 1 as the bytes reach the bucket. */
  onProgress?: (fraction: number) => void,
): Promise<unknown[]> {
  const files = isDataFile(file) ? await prepareDataFile(file) : [file];
  const processed: unknown[] = [];
  for (const prepared of files) {
    processed.push(await uploadAttachmentFile(prepared, target, onProgress));
  }
  return processed;
}

/** The signed PUT. fetch cannot report upload progress, so this uses XHR. */
function putToBucket(
  url: string,
  file: File,
  mimetype: string,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", mimetype);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onProgress?.(event.loaded / event.total);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.send(file);
  });
}

/**
 * Validate, upload and register one file (a PDF or video; in the workshop also a
 * CSV or text file). Throws with a message fit to show the user. Resolves with
 * the process endpoint's response.
 */
async function uploadAttachmentFile(
  file: File,
  target: { uploadUrl: string; process: string; sessionId: string | null },
  onProgress?: (fraction: number) => void,
): Promise<unknown> {
  const kind = attachmentKindOf(file);
  if ((kind === "spreadsheet" || kind === "text") && file.size > MAX_DATA_FILE_BYTES) {
    throw new Error(
      `File is too large (max ${Math.floor(MAX_DATA_FILE_BYTES / (1024 * 1024))} MB)`,
    );
  }
  if (kind === "video") {
    if (file.size > MAX_VIDEO_BYTES) {
      throw new Error(
        `Video is too large (max ${Math.floor(MAX_VIDEO_BYTES / (1024 * 1024))} MB)`,
      );
    }
    const durationSec = await readVideoDuration(file);
    if (durationSec && durationSec > MAX_VIDEO_SECONDS) {
      throw new Error(`Video is too long (max ${MAX_VIDEO_SECONDS / 60} minutes)`);
    }
  }

  const mimetype = mimeFor(file);
  const { data } = await axiosInstance.post<UploadUrlResponse>(target.uploadUrl, {
    sessionId: target.sessionId,
    filename: file.name,
    mimetype,
    extension: extensionOf(file.name) || (kind === "video" ? "mp4" : "pdf"),
  });

  await putToBucket(data.uploadUrl, file, mimetype, onProgress);

  const processed = await axiosInstance.post<unknown>(target.process, {
    uploadToken: data.uploadToken,
  });
  return processed.data;
}

/** A server row as the chat shows it, with what this tab learned while uploading it. */
function toItem(d: ServerAttachment): ChatAttachmentItem {
  return {
    id: d.id,
    fileName: d.fileName ?? (d.type === "video" ? "video" : "document"),
    kind: attachmentKindOfType(d.type),
    state:
      d.state === "completed"
        ? "completed"
        : d.state === "failed"
          ? "error"
          : "processing",
    createdAt: d.createdAt,
    ...(d.error ? { error: d.error } : {}),
    ...(d.canRetry ? { canRetry: true } : {}),
    authorUserId: d.attribution?.createdBy.userId ?? null,
    authorName: d.attribution?.createdBy.userName ?? null,
    durationSec: d.durationSec ?? localDurations.get(d.id) ?? null,
    previewUrl: localPosters.get(d.id) ?? null,
    sourceUrl: d.sourceUrl ?? null,
  };
}

export function useSessionAttachments(
  brandId: string,
  sessionId: string | null,
  customEndpoints?: AttachmentEndpoints,
) {
  const qc = useQueryClient();
  const endpoints = useMemo(
    () => customEndpoints ?? brandEndpoints(brandId),
    [customEndpoints, brandId],
  );
  // Keyed by session so in-flight uploads never bleed across chats.
  const [pendingBySession, setPendingBySession] = useState<
    Record<string, PendingUpload[]>
  >({});

  const query = useQuery({
    queryKey: endpoints.queryKey(sessionId),
    queryFn: async () => {
      const res = await axiosInstance.get<unknown>(endpoints.list, { params: { sessionId } });
      return endpoints.parseList(res.data);
    },
    enabled: !!brandId && !!sessionId,
    // Poll on anything non-terminal, not on state === "processing": a state
    // the backend adds later must keep polling, not spin a chip forever.
    refetchInterval: (q) =>
      (q.state.data ?? []).some((d) => d.state !== "completed" && d.state !== "failed")
        ? 4000
        : false,
    // Freshness here gates a user action — the deferred send waits on it, and a
    // hidden tab used to hold a queued message for up to 16 minutes.
    refetchIntervalInBackground: true,
  });

  const uploadOne = useCallback(
    async (
      file: File,
      targetSessionId: string,
      onUploaded?: (documentId: string) => void,
    ) => {
      const tempId = `${file.name}-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`;
      const kind = attachmentKindOf(file);
      const addPending = (item: PendingUpload) =>
        setPendingBySession((prev) => ({
          ...prev,
          [targetSessionId]: [...(prev[targetSessionId] ?? []), item],
        }));
      const patchPending = (patch: Partial<PendingUpload>) =>
        setPendingBySession((prev) => ({
          ...prev,
          [targetSessionId]: (prev[targetSessionId] ?? []).map((p) =>
            p.tempId === tempId ? { ...p, ...patch } : p,
          ),
        }));
      const dropPending = () =>
        setPendingBySession((prev) => ({
          ...prev,
          [targetSessionId]: (prev[targetSessionId] ?? []).filter(
            (p) => p.tempId !== tempId,
          ),
        }));

      addPending({
        tempId,
        fileName: file.name,
        kind,
        status: "uploading",
        createdAt: new Date().toISOString(),
        progress: 0,
      });

      // A video's thumbnail and length come from the file itself, while it uploads.
      let poster: string | null = null;
      let duration: number | null = null;
      const learned =
        kind === "video"
          ? captureVideoPoster(file).then((read) => {
              poster = read.poster;
              duration = read.duration;
              patchPending({ previewUrl: read.poster, durationSec: read.duration });
            })
          : Promise.resolve();

      // Whole percents only: a progress event per network chunk would re-render
      // the rail dozens of times a second.
      let shown = 0;
      const onProgress = (fraction: number) => {
        const pct = Math.floor(fraction * 100);
        if (pct <= shown) return;
        shown = pct;
        patchPending({ progress: pct / 100 });
      };

      try {
        const processed = await uploadPickedFile(
          file,
          {
            uploadUrl: endpoints.uploadUrl,
            process: endpoints.process,
            sessionId: targetSessionId,
          },
          onProgress,
        );
        await learned;
        for (const result of processed) {
          const documentId = endpoints.processedId(result);
          if (!documentId) continue;
          if (poster) localPosters.set(documentId, poster);
          if (duration) localDurations.set(documentId, duration);
          onUploaded?.(documentId);
        }
        for (const key of endpoints.alsoInvalidate ?? []) {
          void qc.invalidateQueries({ queryKey: key });
        }

        // Refetch the server list (which now carries a "processing" row) BEFORE
        // dropping the client-side pending entry. Otherwise there's a tick where
        // the file is in neither list, and a deferred send watching for
        // "nothing pending" could flush prematurely.
        await qc.invalidateQueries({
          queryKey: endpoints.queryKey(targetSessionId),
        });
        dropPending();
      } catch (error) {
        patchPending({
          status: "error",
          error: error instanceof Error ? error.message : "Upload failed",
        });
      }
    },
    [endpoints, qc],
  );

  const upload = useCallback(
    async (
      files: File[],
      targetSessionId: string,
      /** Called with each new document's id once it is registered. */
      onUploaded?: (documentId: string) => void,
    ) => {
      const accepted: File[] = [];
      const rejected: PendingUpload[] = [];
      for (const file of files) {
        if (
          isPdfFile(file) ||
          isVideoFile(file) ||
          (endpoints.acceptsDataFiles && isDataFile(file))
        ) {
          accepted.push(file);
          continue;
        }
        // A silently dropped file reads as a broken uploader; make it visible.
        rejected.push({
          tempId: `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          fileName: file.name,
          kind: "pdf",
          status: "error",
          createdAt: new Date().toISOString(),
          error: endpoints.acceptsDataFiles ? WORKSHOP_FILE_TYPES_MESSAGE : ONLY_PDF_AND_VIDEO,
        });
      }

      if (rejected.length > 0) {
        setPendingBySession((prev) => ({
          ...prev,
          [targetSessionId]: [...(prev[targetSessionId] ?? []), ...rejected],
        }));
      }
      if (accepted.length === 0) return;
      await Promise.all(accepted.map((f) => uploadOne(f, targetSessionId, onUploaded)));
    },
    [endpoints, uploadOne],
  );

  const retry = useCallback(
    async (documentId: string) => {
      await axiosInstance.post(endpoints.retry(documentId));
      await qc.invalidateQueries({
        queryKey: endpoints.queryKey(sessionId),
      });
      for (const key of endpoints.alsoInvalidate ?? []) {
        void qc.invalidateQueries({ queryKey: key });
      }
    },
    [endpoints, qc, sessionId],
  );

  const removeEndpoint = endpoints.remove;
  const queryKey = endpoints.queryKey;
  /**
   * Take a file out of the chat. Optimistic: it leaves the list at once and
   * comes back, with a toast, if the server refuses. A file refused before it
   * reached the server is only on this device, so it is simply dropped.
   */
  const remove = useCallback(
    async (documentId: string) => {
      if (!sessionId) return;
      const local = (pendingBySession[sessionId] ?? []).some((p) => p.tempId === documentId);
      if (local) {
        setPendingBySession((prev) => ({
          ...prev,
          [sessionId]: (prev[sessionId] ?? []).filter((p) => p.tempId !== documentId),
        }));
        return;
      }
      if (!removeEndpoint) return;
      const key = queryKey(sessionId);
      await qc.cancelQueries({ queryKey: key });
      const before = qc.getQueryData<ServerAttachment[]>(key);
      const removed = before?.find((d) => d.id === documentId);
      qc.setQueryData<ServerAttachment[]>(key, (rows) => rows?.filter((d) => d.id !== documentId));
      try {
        await axiosInstance.delete(removeEndpoint(documentId));
      } catch {
        if (removed) {
          qc.setQueryData<ServerAttachment[]>(key, (rows) =>
            rows && !rows.some((d) => d.id === documentId) ? [...rows, removed] : rows,
          );
        }
        toastQueue.add({
          title: "Couldn't remove the file",
          description: removed?.fileName ?? "Please try again.",
          variant: "error",
        });
      } finally {
        void qc.invalidateQueries({ queryKey: key });
      }
    },
    [pendingBySession, qc, queryKey, removeEndpoint, sessionId],
  );


  const items = useMemo<ChatAttachmentItem[]>(() => {
    // A video the agent attached from a link is shown with the turn that
    // attached it (see `linked`). As its own row it would sort by time among
    // the live tool calls and jump below the reply once the turn settled.
    const server = (query.data ?? []).filter((d) => !d.sourceUrl).map(toItem);
    const pending: ChatAttachmentItem[] = (
      sessionId ? (pendingBySession[sessionId] ?? []) : []
    ).map((p) => ({
      id: p.tempId,
      fileName: p.fileName,
      kind: p.kind,
      state: p.status,
      createdAt: p.createdAt,
      local: true,
      ...(p.error ? { error: p.error } : {}),
      ...(p.progress !== undefined ? { progress: p.progress } : {}),
      previewUrl: p.previewUrl ?? null,
      durationSec: p.durationSec ?? null,
    }));
    return [...server, ...pending].sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt),
    );
  }, [query.data, pendingBySession, sessionId]);

  /** Videos the agent attached from a link, by document id. */
  const linked = useMemo(
    () => new Map((query.data ?? []).filter((d) => d.sourceUrl).map((d) => [d.id, toItem(d)])),
    [query.data],
  );

  return {
    items,
    linked,
    isLoading: query.isLoading,
    upload,
    retry,
    remove,
  };
}
